/**
 * Bridge between Roadmap's bundle-form and the theme's native cart event system.
 * - Listens for Roadmap's 'bundle-added' event
 * - Dispatches the theme's 'cart:update' event so cart drawer opens with animations
 * - Prevents the Roadmap form from redirecting to /cart
 * - Styles the Roadmap button to match the theme
 */
(function () {
  function initBundleBridge() {
    const bundleForms = document.querySelectorAll('bundle-form');
    if (!bundleForms.length) return;

    bundleForms.forEach((form) => {
      // Prevent duplicate initialization
      if (form._bundleBridgeInit) return;
      form._bundleBridgeInit = true;
      // Prevent the Roadmap form's default redirect/reload behavior
      // by intercepting navigation after bundle-added fires
      let bundleJustAdded = false;

      form.addEventListener('bundle-added', async (event) => {
        bundleJustAdded = true;

        // Trigger fly-to-cart animation immediately (before async fetch)
        triggerFlyToCart(form);

        // Fetch actual cart count so we dispatch the correct total
        let cartItemCount = 0;
        try {
          const rootUrl = window.Shopify?.routes?.root || '/';
          const cartRes = await fetch(rootUrl + 'cart.js', {
            headers: { Accept: 'application/json' },
          });
          const cart = await cartRes.json();
          cartItemCount = cart.item_count || 0;
        } catch (e) {
          // Fallback: estimate from event
          const items = (event.detail || {}).items || [];
          cartItemCount = items.reduce((sum, item) => sum + (item.quantity || 1), 0);
        }

        // Dispatch theme's cart:update with the actual total cart count.
        // Using a non-product-form source so cart-icon SETS (not adds) the count.
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: {},
              sourceId: '',
              data: {
                source: 'bundle-form',
                itemCount: cartItemCount,
                productId: '',
              },
            },
          })
        );

        // Reset flag after a tick
        setTimeout(() => {
          bundleJustAdded = false;
        }, 100);
      });

      // Intercept the Roadmap form's navigation (redirect to /cart or page reload)
      // by overriding handlePostAddBehavior if possible
      overridePostAddBehavior(form);
    });
  }

  /**
   * Override the Roadmap form's post-add behavior to prevent redirect.
   * The theme's cart drawer will open instead via the cart:update event.
   */
  function overridePostAddBehavior(form) {
    // Wait for the custom element to be defined and connected
    const observer = new MutationObserver(() => {
      if (form.handlePostAddBehavior || form._settings) {
        // Override the method to do nothing - our event dispatch handles it
        form.handlePostAddBehavior = () => {};
        observer.disconnect();
      }
    });

    // Also try immediately
    if (typeof form.handlePostAddBehavior === 'function') {
      form.handlePostAddBehavior = () => {};
    } else {
      // Watch for the element to initialize
      observer.observe(form, { attributes: true, childList: true });

      // Fallback: override after a short delay
      setTimeout(() => {
        if (typeof form.handlePostAddBehavior === 'function') {
          form.handlePostAddBehavior = () => {};
        }
        observer.disconnect();
      }, 2000);
    }
  }

  /**
   * Trigger the theme's fly-to-cart animation from the bundle form's button
   */
  function triggerFlyToCart(form) {
    const btn = form.querySelector('button[type="submit"]');
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (!btn || !cartIcon) return;

    // Try to get product image for the animation
    const img = form.querySelector('.bundle-component__img');
    const imageSrc = img ? img.src : null;

    if (imageSrc && document.querySelector('fly-to-cart') !== undefined) {
      try {
        const flyEl = document.createElement('fly-to-cart');
        flyEl.classList.add('fly-to-cart--main');
        flyEl.style.setProperty('background-image', `url(${imageSrc})`);
        flyEl.style.setProperty('--start-opacity', '0');
        flyEl.source = btn;
        flyEl.destination = cartIcon;
        document.body.appendChild(flyEl);
      } catch (e) {
        // fly-to-cart element might not be defined, silently fail
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundleBridge);
  } else {
    initBundleBridge();
  }

  // Also re-init if bundle forms are added dynamically
  if (typeof customElements !== 'undefined') {
    customElements.whenDefined('bundle-form').then(initBundleBridge).catch(() => {});
  }
})();
